/**
 * Service for initializing video project structure and files
 */

import { v4 as uuidv4 } from 'uuid';
import api from './api';

export interface ProjectInitializationOptions {
  conversationId: string;
  workingDirectory?: string;
  styleId?: string;
  initGit?: boolean;
  retryOnGitError?: boolean;
}

export interface ProjectStructure {
  projectId: string;
  directories: string[];
  files: Array<{
    path: string;
    content: string;
  }>;
}

// Default design instructions template
const DEFAULT_DESIGN_INSTRUCTIONS = `# Design Instructions for HTML Slides (v7 - Final)

This document outlines the definitive design system for creating full-screen, visually rich, and animated HTML slides.

## 1. Core Setup

- **Tailwind CSS:** All slides **must** include the Tailwind CSS library via CDN.
- **Custom Fonts:** All slides **must** include the 'Poppins' font from Google Fonts.
- **Boilerplate \`<head>\`:**
    \`\`\`html
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Slide Title</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;900&display=swap" rel="stylesheet">
        <style>
            body { font-family: 'Poppins', sans-serif; }
            /* Add animation keyframes here */
        </style>
    </head>
    \`\`\`

## 2. Layout

- **Body as Screen:** The \`<body>\` tag serves as the full-screen container.
    \`\`\`html
    <body class="bg-white w-screen h-screen grid grid-cols-2 items-center gap-20 p-20 box-border overflow-hidden">
    \`\`\`
- **Header/Logo:** The logo is placed in an absolutely positioned div at the top-left.
    \`\`\`html
    <div class="absolute top-20 left-20">
        <a href="#" class="flex items-center">
            <!-- 4K Labs Logo -->
            <svg width="140" height="36" viewBox="0 0 140 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="60" height="36" rx="8" fill="url(#gradient)"/>
              <text x="8" y="25" fill="white" font-size="20" font-weight="bold" font-family="system-ui, -apple-system, sans-serif">4K</text>
              <path d="M38 16L42 18L38 20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M45 16L49 18L45 20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <text x="68" y="25" fill="url(#textGradient)" font-size="22" font-weight="bold" font-family="system-ui, -apple-system, sans-serif">Labs</text>
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#8B5CF6"/>
                  <stop offset="100%" stop-color="#7C3AED"/>
                </linearGradient>
                <linearGradient id="textGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stop-color="#8B5CF6"/>
                  <stop offset="100%" stop-color="#7C3AED"/>
                </linearGradient>
              </defs>
            </svg>
        </a>
    </div>
    \`\`\`

## 3. Typography

- **Main Title:** Use \`.text-8xl\`, \`.font-bold\`, \`.text-gray-900\`, \`.leading-tight\`.
- **Subtitle:** Use \`.text-3xl\`, \`.text-gray-600\`, \`.mt-8\`.
- **Highlighting:** Use a \`<span>\` with Tailwind classes for background, text color, padding, and rounded corners.
    \`\`\`html
    <span class="bg-yellow-400 px-4 rounded-lg">Highlight</span>
    <span class="bg-green-400 text-white px-4 rounded-lg">Highlight</span>
    \`\`\`

## 4. Components

- **Image Container:**
    - A container \`div\` with \`.w-full\`, \`.h-[80%]\`, \`.rounded-3xl\`, \`.overflow-hidden\`, \`.shadow-2xl\`.
    - The \`<img>\` tag inside should have \`.w-full\`, \`.h-full\`, \`.object-cover\`.
- **Chart.js Container:**
    - A \`div\` with \`.relative\`, a fixed size (e.g., \`.w-[500px]\`, \`.h-[500px]\`).
    - A \`<canvas>\` element for the chart.
    - An absolutely positioned \`div\` overlaid for the large text label (\`.absolute\`, \`.inset-0\`, \`.flex\`, \`.items-center\`, \`.justify-center\`, \`.text-8xl\`, \`.font-black\`).

## 5. Animation

- **Keyframes:** Define simple, reusable keyframes for animations.
    \`\`\`css
    @keyframes slideIn {
        from { transform: translateX(-30px); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
    }
    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    \`\`\`
- **Application:** Apply animations using custom classes and staggered delays.
    \`\`\`html
    <h1 class="... animate-slide-in" style="animation-delay: 0.5s;">...</h1>
    <p class="... animate-slide-in" style="animation-delay: 0.8s;">...</p>
    <div class="... animate-fade-in" style="animation-delay: 1s;">...</div>
    \`\`\`

## 6. Responsive Design

- **Aspect Ratio Detection:** Automatically adapt layouts based on detected aspect ratio:
  - **16:9 (1920x1080):** Landscape-optimized layouts with horizontal emphasis
  - **9:16 (1080x1920):** Portrait-optimized layouts with vertical emphasis

## 7. Layout Templates

*This section will be populated with specific layout templates based on analyzed reference images.*

## 8. Color Schemes

*This section will be populated with color palettes detected from reference images.*

## 9. Custom Animations

*This section will be populated with animation patterns detected from reference images.*

## 10. Usage Guidelines

*This section will be populated with specific usage guidelines for each layout template.*
`;

/**
 * Generate initial state.json content with simplified structure
 */
function generateInitialState(projectId: string): string {
  const now = new Date().toISOString();

  const initialState = {
    id: projectId,
    title: projectId, // Initially use projectId, will be updated by LLM
    thumbnail: null,
    userInstructions: null, // User's initial message/prompt, will be set after init
    creation_date: now,
    script_path: null,
    slides: [],
    voice_over: null,
    video_status: "initialized",
    // IMPORTANT: Always null - never expose absolute paths to LLM
    working_directory: null,
    project_metadata: {
      working_directory: null,
      project_root: null,
      state_file_path: null
    }
  };

  return JSON.stringify(initialState, null, 2);
}

/**
 * Create project structure for video generation
 */
export function createProjectStructure(projectId: string): ProjectStructure {
  const directories = [
    'resources',
    'resources/images',
    'resources/documents',
    'resources/other',
    'slides',
    'style'
  ];

  const files = [
    {
      path: 'state.json',
      content: generateInitialState(projectId)
    },
    {
      path: 'style/design_instructions.md',
      content: DEFAULT_DESIGN_INSTRUCTIONS
    },
    {
      path: 'plan.json',
      content: JSON.stringify({
        project_metadata: {
          title: "Video Project",
          description: "Video project description will be added during planning",
          total_slides: 0,
          estimated_duration: 0,
          created_at: new Date().toISOString(),
          aspect_ratio: "16:9"
        },
        slides: []
      }, null, 2)
    },
    {
      path: 'voiceover-script.txt',
      content: 'Voice-over script content will be generated during planning stage.'
    },
    {
      path: 'voiceover-audio.json',
      content: JSON.stringify([], null, 2)
    },
    {
      path: 'voiceover-player.html',
      content: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Voice-over Player</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap" rel="stylesheet">
    <style>
        body {
            font-family: 'Poppins', sans-serif;
            margin: 0;
            padding: 20px;
            background: #f5f5f5;
        }
        .player-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            border-radius: 12px;
            padding: 30px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }
        .audio-controls {
            width: 100%;
            margin: 20px 0;
        }
        .placeholder {
            text-align: center;
            color: #666;
            padding: 40px;
            background: #f9f9f9;
            border-radius: 8px;
            border: 2px dashed #ddd;
        }
    </style>
</head>
<body>
    <div class="player-container">
        <h1>🎙️ Voice-over Audio</h1>
        <div id="audio-container">
            <div class="placeholder">
                <p>Audio will be loaded when generated</p>
                <p><small>This player will automatically update with the generated audio URL</small></p>
            </div>
        </div>
    </div>

    <script>
        // This will be updated by LLM when audio URL is available
        const AUDIO_URL = null; // LLM will replace this with actual URL
        
        function loadAudio() {
            if (AUDIO_URL) {
                const audioContainer = document.getElementById('audio-container');
                audioContainer.innerHTML = \`
                    <audio controls class="audio-controls">
                        <source src="\${AUDIO_URL}" type="audio/mpeg">
                        Your browser does not support the audio element.
                    </audio>
                    <p><strong>Voice-over Script Audio</strong></p>
                    <p><small>URL: \${AUDIO_URL}</small></p>
                \`;
            }
        }
        
        // Load audio when page loads
        window.addEventListener('DOMContentLoaded', loadAudio);
    </script>
</body>
</html>`
    },
    {
      path: 'resources/index-template.html',
      content: `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>{{LLM_VIDEO_TITLE}}</title>
<!--
  Video Presentation Template with WAAPI Transitions
  
  This template provides a complete video presentation system with:
  - Smooth Web Animations API (WAAPI) transitions between scenes
  - Audio-synchronized playback
  - Dual-iframe system for seamless transitions
  - Export-ready timeline controller
  
  For LLM Generation:
  - Replace {{LLM_*}} placeholders with actual content
  - Update the scenes array with your slide files and timing
  - Modify audio sources to match your content
  - Keep the WAAPI system intact for consistent animations
-->
<style>
  /* LLM CREATIVE FREEDOM SECTION - Customize these styles */
  {{LLM_CUSTOM_STYLES}}
  
  /* SYSTEM REQUIRED STYLES - Keep these intact */
  body, html {
    margin: 0;
    padding: 0;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    background-color: #000;
    font-family: 'Poppins', sans-serif;
  }
  #player-container {
    width: var(--viewport-width, 1920px);
    height: var(--viewport-height, 1080px);
    position: absolute;
    box-shadow: 0 0 100px rgba(0,0,0,0.2);
    background-color: #FDFBF5;
    overflow: hidden;
    transform-origin: top left;
  }
  .iframe-wrapper {
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 1;
    transform: scale(1) translateX(0) translateY(0);
    z-index: 1;
  }
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }

  /* WAAPI Transition System - Keep intact for smooth transitions */
  .click-to-start {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0,0,0,0.7);
    color: white;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    z-index: 10;
    cursor: pointer;
    font-size: 48px;
  }
  .play-icon {
      font-size: 120px;
      margin-bottom: 20px;
  }
</style>
</head>
<body>

<!-- LLM CREATIVE FREEDOM SECTION - Add custom overlays, branding, etc. -->
{{LLM_CUSTOM_BODY_CONTENT}}

<!-- SYSTEM REQUIRED STRUCTURE - Keep intact -->
<div id="player-container">
    <div class="iframe-wrapper" id="iframe-a">
        <iframe id="scene-player-a"></iframe>
    </div>
    <div class="iframe-wrapper" id="iframe-b">
        <iframe id="scene-player-b"></iframe>
    </div>
    <div id="click-to-start" class="click-to-start">
        <!-- LLM CREATIVE FREEDOM - Customize play button appearance -->
        {{LLM_PLAY_BUTTON_CONTENT}}
    </div>
</div>

<!-- LLM REQUIRED CONFIGURATION - Update with actual audio URLs -->
<audio id="voice-over" src="{{LLM_VOICE_OVER_URL}}"></audio>
<audio id="background-music" src="{{LLM_BACKGROUND_MUSIC_URL}}" loop></audio>

<script>
  // LLM REQUIRED: Scene Configuration - Replace with actual slide data
  const scenes = {{LLM_SCENES_ARRAY}};
  window.scenes = scenes;

  // WAAPI Transition Types - LLM can customize transition sequence
  const transitions = [
      { out: 'off-screen-left', in: 'off-screen-right' },
      { out: 'off-screen-top', in: 'off-screen-bottom' },
      { out: 'off-screen-zoom-out', in: 'off-screen-fade' },
      { out: 'off-screen-fade', in: 'off-screen-fade' },
      { out: 'off-screen-right', in: 'off-screen-left' },
      { out: 'off-screen-bottom', in: 'off-screen-top' },
      { out: 'off-screen-zoom-in', in: 'off-screen-fade' }
  ];

  // SYSTEM INFRASTRUCTURE - Keep intact for WAAPI functionality
  const playerContainer = document.getElementById('player-container');
  const voiceOver = document.getElementById('voice-over');
  const backgroundMusic = document.getElementById('background-music');
  const clickToStart = document.getElementById('click-to-start');

  let currentSceneIndex = -1;
  let activeIframe = 'a';

  function resizePlayer() {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const cs = getComputedStyle(document.documentElement);
    const pw = parseInt(cs.getPropertyValue('--viewport-width')) || 1920;
    const ph = parseInt(cs.getPropertyValue('--viewport-height')) || 1080;
    const scaleX = viewportWidth / pw;
    const scaleY = viewportHeight / ph;
    const scale = Math.min(scaleX, scaleY);
    playerContainer.style.transform = 'scale(' + scale + ')';
    playerContainer.style.left = ((viewportWidth - pw * scale) / 2) + 'px';
    playerContainer.style.top = ((viewportHeight - ph * scale) / 2) + 'px';
  }

  window.addEventListener('resize', resizePlayer);
  resizePlayer();

  function triggerSlideAnimations(iframe) {
    setTimeout(() => {
      try {
        iframe.contentWindow.postMessage({ type: 'startAnimations' }, '*');
      } catch (error) {
        console.warn('Could not trigger slide animations:', error);
      }
    }, 200);
  }

  // Basic playback functions - LLM will extend these with full WAAPI system
  function playScene(sceneIndex) {
    if (sceneIndex === currentSceneIndex || sceneIndex >= scenes.length) return;
    
    const scene = scenes[sceneIndex];
    const inactiveIframeId = activeIframe === 'a' ? 'b' : 'a';
    const inactivePlayer = document.getElementById('scene-player-' + inactiveIframeId);
    
    inactivePlayer.src = scene.file;
    inactivePlayer.onload = () => {
      triggerSlideAnimations(inactivePlayer);
      activeIframe = inactiveIframeId;
      currentSceneIndex = sceneIndex;
    };
  }

  voiceOver.addEventListener('timeupdate', () => {
    const currentTime = voiceOver.currentTime;
    let sceneIndexToShow = -1;

    for (let i = 0; i < scenes.length; i++) {
        if (scenes[i].endTime > scenes[i].startTime && currentTime >= scenes[i].startTime && currentTime < scenes[i].endTime) {
            sceneIndexToShow = i;
            break;
        }
    }

    if (sceneIndexToShow !== -1 && sceneIndexToShow !== currentSceneIndex) {
        playScene(sceneIndexToShow);
    }
  });

  clickToStart.addEventListener('click', () => {
    clickToStart.style.display = 'none';
    const firstPlayer = document.getElementById('scene-player-a');
    firstPlayer.src = scenes[0].file;
    firstPlayer.onload = () => triggerSlideAnimations(firstPlayer);
    currentSceneIndex = 0;
    voiceOver.play().catch(e => console.error("Voice-over playback failed:", e));
    backgroundMusic.volume = 0.2;
    backgroundMusic.play().catch(e => console.error("Background music playback failed:", e));
  }, { once: true });

  if (scenes.length > 0) {
    document.getElementById('scene-player-a').src = scenes[0].file;
  }
</script>
</body>
</html>`
    },
    {
      path: 'resources/slide-template.html',
      content: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>{{LLM_SLIDE_TITLE}}</title>
    
    <!-- LLM CREATIVE FREEDOM: Add custom fonts -->
    <link href="https://fonts.googleapis.com/css2?family=Gaegu:wght@400;700&family=Poppins:wght@400;600&display=swap" rel="stylesheet">
    
    <style>
        /* SYSTEM REQUIRED STYLES - Keep these base styles intact */
        body, html {
            margin: 0;
            padding: 0;
            width: 1920px;
            height: 1080px;
            font-family: 'Poppins', sans-serif;
            overflow: hidden;
            position: relative;
        }
        
        /* LLM CREATIVE FREEDOM SECTION - Customize these styles */
        {{LLM_CUSTOM_STYLES}}
        
        /* SYSTEM REQUIRED: Animation elements start hidden */
        .animate-element {
            opacity: 0;
        }
        
        /* LLM CREATIVE FREEDOM: Add your animation keyframes here */
        {{LLM_ANIMATION_KEYFRAMES}}
        
        /* Example animation classes - LLM can customize these */
        .animate-fade-in {
            animation: fadeIn 1s ease-out forwards;
        }
        
        .animate-slide-up {
            animation: slideUp 0.8s ease-out forwards;
        }
        
        .animate-pop-in {
            animation: popIn 1s ease-out forwards;
        }
        
        @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
        
        @keyframes slideUp {
            from { opacity: 0; transform: translateY(50px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes popIn {
            0% { opacity: 0; transform: scale(0.8); }
            60% { opacity: 1; transform: scale(1.05); }
            100% { opacity: 1; transform: scale(1); }
        }
        
    </style>
</head>
<body>
    <!-- SYSTEM REQUIRED: Logo header - Keep intact -->
    <div style="position: absolute; top: 50px; left: 50px; z-index: 10;">
        <a href="#" style="display: flex; align-items: center; text-decoration: none;">
            <!-- 4K Labs Logo -->
            <svg width="140" height="36" viewBox="0 0 140 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="60" height="36" rx="8" fill="url(#gradient4k)"/>
              <text x="8" y="25" fill="white" font-size="20" font-weight="bold" font-family="system-ui, -apple-system, sans-serif">4K</text>
              <path d="M38 16L42 18L38 20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M45 16L49 18L45 20" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <text x="68" y="25" fill="url(#textGradient4k)" font-size="22" font-weight="bold" font-family="system-ui, -apple-system, sans-serif">Labs</text>
              <defs>
                <linearGradient id="gradient4k" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#8B5CF6"/>
                  <stop offset="100%" stop-color="#7C3AED"/>
                </linearGradient>
                <linearGradient id="textGradient4k" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stop-color="#8B5CF6"/>
                  <stop offset="100%" stop-color="#7C3AED"/>
                </linearGradient>
              </defs>
            </svg>
        </a>
    </div>
    
    <!-- LLM CREATIVE FREEDOM SECTION - Design your slide content here -->
    <div class="slide-container">
        {{LLM_SLIDE_CONTENT}}
    </div>

    <!-- SYSTEM REQUIRED: WAAPI Animation Controller - Keep intact -->
    <script>
        // WAAPI-First Animation System
        class SlideAnimationController {
            constructor() {
                this.animations = [];
                this.totalDuration = 0;
                this.mode = 'live'; // 'live' or 'capture'
            }

            createAnimations() {
                // LLM CREATIVE FREEDOM: Define your animations here
                {{LLM_ANIMATION_LOGIC}}
                
                // SYSTEM REQUIRED: Pause all animations initially
                this.animations.forEach(anim => {
                    anim.animation.pause();
                });

                // Calculate total duration
                this.totalDuration = Math.max(...this.animations.map(anim => 
                    (anim.animation.effect.getTiming().delay || 0) + 
                    (anim.animation.effect.getTiming().duration || 0)
                ));
            }

            playLive() {
                this.mode = 'live';
                this.animations.forEach(anim => {
                    anim.animation.play();
                });
            }

            setupCapture() {
                this.mode = 'capture';
                this.animations.forEach(anim => {
                    anim.animation.currentTime = 0;
                    anim.animation.pause();
                });
            }

            seekToTime(timeMs) {
                this.animations.forEach(anim => {
                    anim.animation.currentTime = timeMs;
                });
            }

            getTotalDuration() {
                return this.totalDuration;
            }
        }

        // Initialize controller
        const slideController = new SlideAnimationController();
        window.slideController = slideController;

        // SYSTEM REQUIRED: Standard interface - Keep intact
        window.addEventListener('message', (event) => {
            if (event.data.type === 'startAnimations') {
                slideController.createAnimations();
                slideController.playLive();
            } else if (event.data.type === 'setupCapture') {
                slideController.createAnimations();
                slideController.setupCapture();
            } else if (event.data.type === 'seekToTime') {
                slideController.seekToTime(event.data.timeMs);
            } else if (event.data.type === 'getTotalDuration') {
                event.source.postMessage({
                    type: 'totalDuration',
                    duration: slideController.getTotalDuration()
                }, '*');
            }
        });
    </script>
</body>
</html>`
    },
    {
      path: 'index.html',
      content: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Video Project</title>
    <style>
        body {
            margin: 0;
            padding: 20px;
            font-family: Arial, sans-serif;
            background-color: #f5f5f5;
        }
        .container {
            max-width: 1200px;
            margin: 0 auto;
            text-align: center;
        }
        .placeholder {
            background: white;
            border: 2px dashed #ccc;
            border-radius: 10px;
            padding: 60px 20px;
            margin: 20px 0;
        }
        .placeholder h2 {
            color: #666;
            margin-bottom: 10px;
        }
        .placeholder p {
            color: #999;
            margin: 0;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>Video Project</h1>
        <div class="placeholder">
            <h2>🎬 Video Player</h2>
            <p>Your video will appear here once slides and audio are generated.</p>
            <p><small>This file will be replaced during video assembly stage.</small></p>
        </div>
    </div>
</body>
</html>`
    }
  ];

  return {
    projectId,
    directories,
    files
  };
}

/**
 * Initialize video project for a conversation
 */
export const projectInitializationService = {
  /**
   * Initialize a new video project structure with retry logic
   */
  async initializeProject(options: ProjectInitializationOptions): Promise<ProjectStructure> {
    const projectId = uuidv4();
    const projectStructure = createProjectStructure(projectId);

    const maxRetries = options.retryOnGitError ? 3 : 1;
    let lastError: any = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Call backend API to create the project structure
        const response = await api.post('/project/initialize', {
          conversationId: options.conversationId,
          workingDirectory: options.workingDirectory,
          styleId: options.styleId,
          projectStructure,
          initGit: options.initGit ?? true
        });

        // Success - log and return
        console.log(`✅ Project initialized successfully on attempt ${attempt}`);
        return response.data.projectStructure;

      } catch (error: any) {
        lastError = error;
        console.error(`Attempt ${attempt} failed to initialize project:`, error);

        // Check if it's a git-related error
        const errorMessage = error?.response?.data?.error || error?.message || '';
        const isGitError = errorMessage.toLowerCase().includes('git') ||
                          errorMessage.includes('HEAD') ||
                          errorMessage.includes('repository');

        if (isGitError && attempt < maxRetries) {
          console.log(`🔄 Git error detected, retrying (${attempt}/${maxRetries})...`);

          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));

          // Try with git disabled if this is the last retry
          if (attempt === maxRetries - 1) {
            console.log('⚠️ Attempting without git initialization...');
            try {
              const fallbackResponse = await api.post('/project/initialize', {
                conversationId: options.conversationId,
                workingDirectory: options.workingDirectory,
                styleId: options.styleId,
                projectStructure,
                initGit: false // Disable git on fallback
              });

              console.log('✅ Project initialized without git');

              // Try to initialize git separately
              setTimeout(async () => {
                try {
                  await api.post(`/sessions/${options.conversationId}/git/init`, {});
                  console.log('✅ Git initialized separately');
                } catch (gitError) {
                  console.warn('⚠️ Could not initialize git separately:', gitError);
                }
              }, 2000);

              return fallbackResponse.data.projectStructure;
            } catch (fallbackError) {
              console.error('Fallback initialization also failed:', fallbackError);
            }
          }
        } else if (!isGitError) {
          // Non-git error, don't retry
          break;
        }
      }
    }

    // All attempts failed
    const errorMessage = lastError?.response?.data?.error ||
                        lastError?.message ||
                        'Failed to initialize video project structure';

    console.error('❌ Failed to initialize project after all attempts:', lastError);

    // Provide a more helpful error message
    if (errorMessage.includes('HEAD') || errorMessage.includes('git')) {
      throw new Error(`Git initialization failed: ${errorMessage}. The project may still work without version control.`);
    } else {
      throw new Error(errorMessage);
    }
  },

  /**
   * Check if a conversation has an initialized project
   */
  async checkProjectStatus(conversationId: string): Promise<{
    initialized: boolean;
    projectId?: string;
    workingDirectory?: string;
  }> {
    try {
      const response = await api.get(`/project/status/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to check project status:', error);
      return { initialized: false };
    }
  }
};